/**
 * TimeTracker – Timer widget for client page (beside Time Entries).
 * When clientId is passed, uses that client; no client selector.
 * GET running-timer on mount; Start/Stop flows.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import api from '../api';

const formatElapsed = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

const TimeTracker = ({ clientId, onEntrySaved }) => {
  const [timerState, setTimerState] = useState(null); // null=loading, 'idle', 'running'
  const [runningData, setRunningData] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [stopForm, setStopForm] = useState({ description: '', billable: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stopError, setStopError] = useState(null);
  const [startLoading, setStartLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);

  const fetchRunningTimer = useCallback(async () => {
    try {
      const res = await api.get('/api/time-entries/running-timer/');
      const data = res.data;
      if (data?.running && data?.timer_started_at) {
        setTimerState('running');
        setRunningData({
          id: data.id,
          client_id: data.client_id,
          client_name: data.client_name || '',
          timer_started_at: data.timer_started_at,
        });
      } else {
        setTimerState('idle');
        setRunningData(null);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setError(msg);
      setTimerState('idle');
      setRunningData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRunningTimer();
  }, [fetchRunningTimer]);

  // Timer interval when running
  useEffect(() => {
    if (timerState !== 'running' || !runningData?.timer_started_at) return;

    const updateElapsed = () => {
      const started = new Date(runningData.timer_started_at).getTime();
      const now = Date.now();
      setElapsedSeconds((now - started) / 1000);
    };

    updateElapsed(); // immediate
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [timerState, runningData?.timer_started_at]);

  const handleStartTimer = async () => {
    const idToUse = clientId ?? runningData?.client_id ?? null;
    setStartLoading(true);
    setError(null);
    try {
      await api.post('/api/time-entries/start-timer/', { client_id: idToUse });
      await fetchRunningTimer();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setError(msg);
    } finally {
      setStartLoading(false);
    }
  };

  const handleStopClick = () => {
    setStopModalOpen(true);
    setStopForm({ description: '', billable: true });
    setStopError(null);
  };

  const handleStopSave = async () => {
    const desc = (stopForm.description || '').trim();
    if (desc.length < 3) {
      setStopError('Description must be at least 3 characters.');
      return;
    }

    setStopLoading(true);
    setStopError(null);
    try {
      await api.post('/api/time-entries/stop-timer/', {
        description: desc,
        billable: stopForm.billable,
      });
      setStopModalOpen(false);
      await fetchRunningTimer();
      onEntrySaved?.();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setStopError(msg);
    } finally {
      setStopLoading(false);
    }
  };

  if (loading && timerState === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mr: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {timerState === 'idle' && (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartTimer}
            disabled={startLoading}
          >
            {startLoading ? 'Starting...' : 'Start Timer'}
          </Button>
        )}

        {timerState === 'running' && (
          <>
            <Typography
              component="span"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'text.primary',
                minWidth: 72,
              }}
            >
              {formatElapsed(elapsedSeconds)}
            </Typography>
            {runningData?.client_name && (
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {runningData.client_name}
              </Typography>
            )}
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={handleStopClick}
            >
              Stop
            </Button>
          </>
        )}
      </Box>

      <Dialog open={stopModalOpen} onClose={() => !stopLoading && setStopModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Stop Timer</DialogTitle>
        <DialogContent>
          {stopError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStopError(null)}>
              {stopError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            placeholder="What did you work on?"
            value={stopForm.description}
            onChange={(e) => setStopForm((f) => ({ ...f, description: e.target.value }))}
            required
            helperText="Minimum 3 characters"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={stopForm.billable}
                onChange={(e) => setStopForm((f) => ({ ...f, billable: e.target.checked }))}
              />
            }
            label="Billable"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopModalOpen(false)} disabled={stopLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStopSave} disabled={stopLoading || (stopForm.description || '').trim().length < 3}>
            {stopLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimeTracker;
